// SegmentTimePrecisionEditor.jsx — v3
// NSL Edu Editor — Precision Time Editor
// Prefix: spe-*
//
// NEW in v3:
//   • previewCut(t, side) — seeks 2s before cut, plays, auto-pauses. Hear the cut.
//   • Linked mode — changing FIN auto-updates next seg's INICIO and viceversa.
//   • Propagation badge — shows which adjacent segment will be updated.
//
// Props:
//   seg           {id, startTime, endTime, ...}
//   prevSeg       {id, startTime, endTime} | null
//   nextSeg       {id, startTime, endTime} | null
//   onUpdate      ({startTime?, endTime?}, propagate?) => void
//   playerRef     ref to YT player
//   videoDuration number — total video seconds

import React, { useState, useCallback, useRef, useEffect } from 'react';
import './SegmentTimePrecisionEditor.css';

// ── Helpers ────────────────────────────────────────────────────────────

const fmtP = (s) => {
    if (s == null || isNaN(s)) return '--:--.0';
    const neg = s < 0;
    const a = Math.abs(s);
    const m = Math.floor(a / 60);
    const sec = Math.floor(a % 60).toString().padStart(2, '0');
    const d = Math.round((a % 1) * 10) % 10;
    return `${neg ? '-' : ''}${m}:${sec}.${d}`;
};

const fmtM = (s) => {
    const a = Math.abs(s);
    const m = Math.floor(a / 60);
    const sec = Math.floor(a % 60).toString().padStart(2, '0');
    return `${m}:${sec}`;
};

const parseTime = (str) => {
    const s = str.trim().replace(',', '.');
    const mmss = s.match(/^(\d+):(\d{1,2})(?:\.(\d*))?$/);
    if (mmss) {
        const frac = mmss[3] ? parseFloat('0.' + mmss[3]) : 0;
        return +mmss[1] * 60 + +mmss[2] + frac;
    }
    const n = parseFloat(s);
    return isNaN(n) ? null : n;
};

const r1 = (v) => Math.round(v * 10) / 10;
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

// Preview config
//  Cinco segundos antes del corte, que es lo que pidió Rob: «cómo lo escucho de
//  principio a fin, o sólo 5 seg antes, para medir el texto con el vídeo». Con
//  dos y medio no da tiempo a situarse en la frase.
const PREVIEW_PRE = 5.0;  // segundos antes del corte
const PREVIEW_POST = 3.0;  // segundos después, para oír cómo sigue
const PREVIEW_TOTAL_MS = (PREVIEW_PRE + PREVIEW_POST) * 1000;

// ══════════════════════════════════════════════════════════════════════
//  COMPONENT
// ══════════════════════════════════════════════════════════════════════

export default function SegmentTimePrecisionEditor({
    seg,
    prevSeg = null,
    nextSeg = null,
    onUpdate,
    playerRef,
    videoDuration = 0,
}) {
    const segStart = seg.startTime ?? 0;
    const segEnd = seg.endTime ?? segStart + 1;
    const total = Math.max(videoDuration, segEnd + 5, 60);

    //  ── HASTA DÓNDE SE PUEDE MOVER (07-10-2026) ───────────────────────
    //  Rob: «puedo mover los parámetros con clic sostenido de principio a fin,
    //  no tiene límites... al superarlo es un error grave, porque no respeta a
    //  los de atrás con su límite; los que sí debe afectar es a los de
    //  adelante».
    //
    //  Tenía razón: el INICIO llegaba hasta el 0:00 y el FIN hasta más allá del
    //  final del vídeo, pasando por encima de los fragmentos vecinos. Arrastrar
    //  uno podía dejar al de atrás con duración negativa, y eso no se ve: se ve
    //  después, cuando el alumno oye una pregunta sobre algo que no ha sonado.
    //
    //  MINIMO es lo que tiene que quedarle al vecino para seguir existiendo. Si
    //  hiciera falta más, se mueve el vecino: lo que no puede es desaparecer
    //  porque alguien arrastró de más en otro sitio.
    const MINIMO = 0.5;
    const topeInicio = Math.max(
        prevSeg ? (prevSeg.startTime ?? 0) + MINIMO : 0,
        0,
    );
    const topeFin = nextSeg
        ? Math.max((nextSeg.endTime ?? total) - MINIMO, segStart + MINIMO)
        : Math.max(videoDuration || total, segStart + MINIMO);

    const limites = {
        start: { min: topeInicio, max: segEnd - MINIMO },
        end: { min: segStart + MINIMO, max: topeFin },
    };

    //  Por qué no se puede pasar de ahí, dicho con el número del vecino: «hasta
    //  el 1:56.3» no explica nada; «el #11 empieza ahí» sí.
    const porQueElTope = (field, hacia) => {
        if (field === 'start') {
            return hacia === 'min'
                ? (prevSeg ? `el fragmento #${prevSeg.id + 1} tiene que quedarse con algo`
                           : 'es el principio del vídeo')
                : 'el FIN de este fragmento está ahí';
        }
        return hacia === 'min'
            ? 'el INICIO de este fragmento está ahí'
            : (nextSeg ? `el fragmento #${nextSeg.id + 1} tiene que quedarse con algo`
                       : 'es el final del vídeo');
    };

    const [editField, setEditField] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [toastMsg, setToastMsg] = useState('');
    const [linked, setLinked] = useState(true);   // propagate to adjacent segs
    const [previewing, setPreviewing] = useState(null);   // 'start' | 'end' | null

    //  ── CLIC ES ELEGIR; ARRASTRAR ES MOVER (07-10-2026) ───────────────
    //  Rob: «vi perfectamente la línea del tiempo en editar, pero le di clic y
    //  se modificó al darle clic... si sólo intento darle clic en un punto
    //  inexacto se modifica, eso no lo quiero porque rompe con todo».
    //
    //  Era verdad y era grave. El `mouseup` del handle escribía SIEMPRE el
    //  tiempo de donde estuviera el ratón, aunque no se hubiera movido ni un
    //  píxel — y el ratón, al pulsar el handle, está en medio de sus veinte
    //  píxeles, no en su borde. O sea que tocarlo lo descuadraba.
    //
    //  Ahora un clic sólo ELIGE. Para mover hay que arrastrar de verdad: tres
    //  píxeles, que es más de lo que tiembla una mano al pulsar y menos de lo
    //  que mueve quien quiere mover.
    const [elegido, setElegido] = useState(null);      // 'start' | 'end' | null
    const [moviendo, setMoviendo] = useState(null);    // mientras se arrastra

    //  ── VOLVER AL PUNTO ORIGINAL (14-10-2026) ─────────────────────────
    //  Rob: «integramos un sistema en el editor de música que si cometía un
    //  error se quedaba registrado el pasado, y si me perdía daba clic y se
    //  regresaba al segundo original. Me gustaría integrarlo también al editor
    //  de vídeos».
    //
    //  Una foto de cómo estaba este fragmento al abrir el panel. Un error de
    //  dedo aquí no se arregla «poniendo el número de antes», porque para
    //  entonces ya no se sabe cuál era: se han dado seis toques a las flechas y
    //  el número de partida se fue con el primero.
    const [original, setOriginal] = useState({ start: null, end: null });

    useEffect(() => {
        setOriginal({ start: seg.startTime ?? null, end: seg.endTime ?? null });
        setElegido(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [seg.id]);

    const toastTimer = useRef(null);
    const previewTimer = useRef(null);
    const trackRef = useRef(null);
    const dragging = useRef(null);

    // ── Toast ────────────────────────────────────────────────────────
    const flash = useCallback((msg, duration = 1800) => {
        clearTimeout(toastTimer.current);
        setToastMsg(msg);
        toastTimer.current = setTimeout(() => setToastMsg(''), duration);
    }, []);

    // ── Seek ────────────────────────────────────────────────────────
    //  `callado` para cuando se acaba de enseñar el motivo de un tope: su
    //  «▶ 0:09.5» lo borraba al instante y Rob se quedaba sin saber por qué el
    //  handle había frenado. Un aviso que dura cien milisegundos es no avisar.
    const seek = useCallback((t, callado = false) => {
        try { playerRef?.current?.seekTo(Math.max(0, t), true); } catch (_) { }
        if (!callado) flash(`▶ ${fmtP(t)}`);
    }, [playerRef, flash]);

    // ── Preview cut — seeks 2.5s before and auto-pauses ─────────────
    const previewCut = useCallback((t, side) => {
        if (!playerRef?.current) return;
        clearTimeout(previewTimer.current);
        setPreviewing(side);

        const seekTo = Math.max(0, t - PREVIEW_PRE);
        const label = side === 'start' ? 'INICIO' : 'FIN';

        try {
            playerRef.current.seekTo(seekTo, true);
            playerRef.current.playVideo();
        } catch (_) { }

        flash(`🎧 Escuchando corte ${label}…`, PREVIEW_TOTAL_MS + 200);

        previewTimer.current = setTimeout(() => {
            try { playerRef.current.pauseVideo(); } catch (_) { }
            setPreviewing(null);
            flash(`⏸ Corte ${label} en ${fmtP(t)}`);
        }, PREVIEW_TOTAL_MS);
    }, [playerRef, flash]);

    const stopPreview = useCallback(() => {
        clearTimeout(previewTimer.current);
        setPreviewing(null);
        try { playerRef?.current?.pauseVideo(); } catch (_) { }
        flash('⏸ Preview detenido');
    }, [playerRef, flash]);

    // ── Nudge ───────────────────────────────────────────────────────
    //  Un solo sitio donde se escribe un tiempo, y un solo sitio donde se
    //  recorta. Antes cada camino —las flechas, los botones, el arrastre, la
    //  casilla— tenía su propio `clamp` con números distintos, y por eso unos
    //  respetaban al vecino y otros no.
    //  Devuelve el valor que de verdad quedó y si hubo que recortarlo.
    const ponerTiempo = useCallback((field, crudo, propaga) => {
        const lim = field === 'start' ? limites.start : limites.end;
        const val = r1(clamp(crudo, lim.min, lim.max));
        let recortado = false;
        if (crudo < lim.min - 0.05) {
            flash(`⛔ Hasta ${fmtP(lim.min)}: ${porQueElTope(field, 'min')}`, 2600);
            recortado = true;
        } else if (crudo > lim.max + 0.05) {
            flash(`⛔ Hasta ${fmtP(lim.max)}: ${porQueElTope(field, 'max')}`, 2600);
            recortado = true;
        }
        onUpdate(field === 'start' ? { startTime: val } : { endTime: val }, propaga);
        return { val, recortado };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [segStart, segEnd, prevSeg, nextSeg, videoDuration, total, onUpdate, flash]);

    /**
     * Devolver un corte a donde estaba al abrir el panel.
     *
     * Rob lo pidió con estas palabras: «si cometía un error se quedaba
     * registrado el pasado, y si me perdía daba clic y se regresaba al segundo
     * original». Ya lo tenían las canciones; esto es lo mismo para los vídeos.
     *
     * Pasa por `ponerTiempo` como todo lo demás, así que respeta los topes de
     * los vecinos. Si mientras tanto el de al lado se movió y el sitio de antes
     * ya no cabe, se queda en el tope y lo dice — que es mejor que volver a un
     * sitio que ahora pisa a otro.
     */
    const deshacer = useCallback((field) => {
        const antes = field === 'start' ? original.start : original.end;
        if (typeof antes !== 'number') return;
        const r = ponerTiempo(field, antes, linked);
        seek(r.val, r.recortado);
        if (!r.recortado) {
            flash(`↩ ${field === 'start' ? 'INICIO' : 'FIN'} devuelto a ${fmtP(antes)}`);
        }
    }, [original, ponerTiempo, seek, linked, flash]);

    const nudge = useCallback((field, delta) => {
        const base = field === 'start' ? segStart : segEnd;
        const { val, recortado } = ponerTiempo(field, base + delta, linked);
        seek(val, recortado);
    }, [segStart, segEnd, ponerTiempo, seek, linked]);

    // ── Commit typed value ──────────────────────────────────────────
    const commitEdit = useCallback((field) => {
        const parsed = parseTime(editValue);
        //  Por el mismo sitio que todo lo demás: tecleando un tiempo tampoco se
        //  puede invadir al vecino.
        if (parsed !== null) {
            const { val, recortado } = ponerTiempo(field, parsed, linked);
            seek(val, recortado);
        }
        setEditField(null);
    }, [editValue, ponerTiempo, seek, linked]);

    //  ── OÍR EL FRAGMENTO ENTERO ───────────────────────────────────────
    //  Rob: «digamos que ya posiciono los indicadores... pero cómo lo escucho
    //  de principio a fin, o sólo 5 seg antes, para medir el texto con el
    //  vídeo».
    //
    //  Se para EN el final, mirando el reloj, no con un temporizador: un
    //  temporizador de cuarenta segundos llega tarde, y lo que se viene a
    //  comprobar aquí es justo si el corte cae donde dice.
    const relojDelFragmento = useRef(null);

    const paraDeOir = useCallback(() => {
        clearInterval(relojDelFragmento.current);
        relojDelFragmento.current = null;
        try { playerRef?.current?.pauseVideo(); } catch (_) { }
        setPreviewing(null);
    }, [playerRef]);

    const oyeElFragmento = useCallback(() => {
        if (!playerRef?.current) return;
        clearInterval(relojDelFragmento.current);
        clearTimeout(previewTimer.current);
        setPreviewing('todo');
        try {
            playerRef.current.seekTo(Math.max(0, segStart), true);
            playerRef.current.playVideo();
        } catch (_) { }
        flash(`▶ ${fmtP(segStart)} → ${fmtP(segEnd)}`, 2000);
        relojDelFragmento.current = setInterval(() => {
            try {
                if (playerRef.current.getCurrentTime() >= segEnd) {
                    playerRef.current.pauseVideo();
                    playerRef.current.seekTo(segEnd, true);
                    clearInterval(relojDelFragmento.current);
                    relojDelFragmento.current = null;
                    setPreviewing(null);
                    flash(`⏸ Fin del fragmento, ${fmtP(segEnd)}`);
                }
            } catch (_) { }
        }, 150);
    }, [playerRef, segStart, segEnd, flash]);

    //  Al cerrar el editor —o al cambiar de fragmento— no puede quedarse un
    //  reloj suelto parando un vídeo que ya no es el suyo.
    useEffect(() => () => clearInterval(relojDelFragmento.current), []);

    // ── Timeline drag ───────────────────────────────────────────────
    const tlPosToTime = (clientX) => {
        if (!trackRef.current) return 0;
        const rect = trackRef.current.getBoundingClientRect();
        const pct = clamp((clientX - rect.left) / rect.width, 0, 1);
        return r1(pct * total);
    };

    //  Tres píxeles. Menos, y el temblor de pulsar ya cuenta como arrastrar;
    //  más, y quien quiere mover poco tiene que pasarse para empezar.
    const UMBRAL_PX = 3;

    const onTlDown = (e, field) => {
        e.preventDefault();
        dragging.current = field;
        setElegido(field);

        const xInicial = e.touches ? e.touches[0].clientX : e.clientX;
        let seMovio = false;

        const escribe = (t, propaga) => ponerTiempo(dragging.current, t, propaga);

        const move = (ev) => {
            const cx = ev.touches ? ev.touches[0].clientX : ev.clientX;
            if (!seMovio) {
                if (Math.abs(cx - xInicial) < UMBRAL_PX) return;
                seMovio = true;
                setMoviendo(field);
            }
            escribe(tlPosToTime(cx), false);
        };

        const up = (ev) => {
            const cx = ev.changedTouches ? ev.changedTouches[0].clientX : ev.clientX;
            if (seMovio) {
                //  Al soltar, arrastra también al vecino si están vinculados.
                //  Y se busca EL VALOR RECORTADO, no donde cayó el ratón: si se
                //  arrastró más allá del tope, el vídeo tiene que ir a donde
                //  quedó el corte, no a donde quiso llegar la mano.
                const r = escribe(tlPosToTime(cx), linked);
                seek(r.val, r.recortado);
            } else {
                //  No se movió: fue un clic. Se queda elegido —para poder
                //  afinarlo con las flechas— y el vídeo va a DONDE ESTÁ, no a
                //  donde cayó el ratón. Nada cambia de sitio.
                seek(field === 'start' ? segStart : segEnd);
            }
            seMovio = false;
            setMoviendo(null);
            dragging.current = null;
            document.removeEventListener('mousemove', move);
            document.removeEventListener('mouseup', up);
            document.removeEventListener('touchmove', move);
            document.removeEventListener('touchend', up);
        };

        document.addEventListener('mousemove', move);
        document.addEventListener('mouseup', up);
        document.addEventListener('touchmove', move, { passive: false });
        document.addEventListener('touchend', up);
    };

    //  ── LAS FLECHAS, SOBRE EL HANDLE ELEGIDO ──────────────────────────
    //  Rob: «como somos precisos, lo ideal es modificar con las flechas hasta
    //  encontrar el milisegundo exacto». Arrastrar sirve para acercarse; la
    //  décima se busca con el teclado, sin soltar la vista del vídeo.
    //
    //  No se escucha nada si el cursor está escribiendo: en esta misma pantalla
    //  hay una casilla donde se teclea el tiempo a mano, y allí las flechas son
    //  suyas.
    useEffect(() => {
        if (!elegido) return;
        const enUnCampo = (el) => {
            const t = (el?.tagName || '').toLowerCase();
            return t === 'input' || t === 'textarea' || el?.isContentEditable;
        };
        const alPulsar = (ev) => {
            if (enUnCampo(ev.target)) return;
            const haciaDonde = ev.key === 'ArrowLeft' || ev.key === 'ArrowDown' ? -1
                : ev.key === 'ArrowRight' || ev.key === 'ArrowUp' ? 1 : 0;
            if (!haciaDonde) return;
            ev.preventDefault();
            //  Una décima a secas, un segundo con Shift, diez con Ctrl: los
            //  mismos tres tamaños que los botones de al lado.
            const paso = ev.ctrlKey || ev.metaKey ? 10 : ev.shiftKey ? 1 : 0.1;
            nudge(elegido, haciaDonde * paso);
        };
        window.addEventListener('keydown', alPulsar);
        return () => window.removeEventListener('keydown', alPulsar);
    }, [elegido, nudge]);

    // ── Timeline metrics ─────────────────────────────────────────────
    const lp = clamp((segStart / total) * 100, 0, 99);
    const ep = clamp((segEnd / total) * 100, 1, 100);
    const wp = Math.max(ep - lp, 0.3);

    // ── Field row renderer ───────────────────────────────────────────
    const renderField = (field) => {
        const val = field === 'start' ? segStart : segEnd;
        const isEditing = editField === field;
        const color = field === 'start' ? 'var(--spe-green)' : 'var(--spe-orange)';
        const label = field === 'start' ? 'INICIO' : 'FIN';
        const side = field === 'start' ? 'start' : 'end';
        const isPrev = previewing === side;

        // Adjacent link info
        const linkTarget = field === 'start' ? prevSeg : nextSeg;
        const linkLabel = field === 'start' ? 'seg anterior' : 'sig. segmento';

        //  Adónde devolvería el ↩, y si hay algo que devolver. Con una centésima
        //  de margen: los tiempos van en décimas y un `!==` entre números con
        //  coma deja el botón encendido por un redondeo que nadie ve.
        const elOriginal = field === 'start' ? original.start : original.end;
        const sinCambios = typeof elOriginal !== 'number' || typeof val !== 'number'
            || Math.abs(val - elOriginal) < 0.05;

        return (
            <div className="spe-field-row" style={{ '--fc': color }}>
                <span className="spe-lbl">{label}</span>

                {isEditing ? (
                    <input
                        className="spe-time-inp"
                        value={editValue}
                        autoFocus
                        placeholder="M:SS.d"
                        onChange={e => setEditValue(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') { commitEdit(field); return; }
                            if (e.key === 'Escape') { setEditField(null); return; }
                            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                                e.preventDefault();
                                const sign = e.key === 'ArrowUp' ? 1 : -1;
                                const step = e.ctrlKey ? 10 : e.shiftKey ? 1 : 0.1;
                                nudge(field, sign * step);
                                setEditValue(fmtP(r1((field === 'start' ? segStart : segEnd) + sign * step)));
                            }
                        }}
                        onBlur={() => commitEdit(field)}
                        onFocus={e => e.target.select()}
                    />
                ) : (
                    <button
                        className="spe-time-disp"
                        onClick={() => { setEditValue(fmtP(val)); setEditField(field); }}
                        title="Click para editar"
                    >
                        {fmtP(val)}
                    </button>
                )}

                {/* Rob, 14-10-2026: «tenemos un menos y más 10s, ese no lo
                    ocupé; lo que sí ocupo es −1s −.5 −.1 +.1 +.5 +1s».
                    Diez segundos es media docena de fragmentos de este vídeo:
                    para saltar tan lejos se arrastra el handle. El medio
                    segundo es el paso que faltaba entre la décima y el
                    segundo, que es donde se afina de verdad. */}
                <div className="spe-nudges">
                    <button className="spe-ndg n" onClick={() => nudge(field, -1)}>−1s</button>
                    <button className="spe-ndg n" onClick={() => nudge(field, -.5)}>−.5</button>
                    <button className="spe-ndg n" onClick={() => nudge(field, -.1)}>−.1</button>
                    <div className="spe-sep" />
                    <button className="spe-ndg p" onClick={() => nudge(field, .1)}>+.1</button>
                    <button className="spe-ndg p" onClick={() => nudge(field, .5)}>+.5</button>
                    <button className="spe-ndg p" onClick={() => nudge(field, 1)}>+1s</button>
                </div>

                {/* ── OÍR EL CORTE ─────────────────────────────────────
                    Rob: «tengo estos iconos que no me sirven de nada». Eran un
                    🎧 y un ▶ sin palabra ninguna, y el ▶ sólo movía el vídeo
                    sin reproducir. Ahora queda uno, dice lo que hace, y hace lo
                    que de verdad sirve: oír los cinco segundos de antes para
                    comprobar si el corte cae donde tiene que caer. */}
                {isPrev ? (
                    <button
                        className="spe-preview-btn playing"
                        onClick={stopPreview}
                        title="Detener"
                    >
                        <span className="spe-preview-pulse" />
                        ⏸ Parar
                    </button>
                ) : (
                    <button
                        className="spe-preview-btn"
                        onClick={() => previewCut(val, side)}
                        title={`Reproduce desde ${fmtP(Math.max(0, val - PREVIEW_PRE))} y se para sola`}
                    >
                        🎧 Oír −5 s
                    </button>
                )}

                {/* ↩ VOLVER AL PUNTO ORIGINAL ──────────────────────
                    Lleva el número DENTRO: un ↩ pelado no dice adónde va, y para
                    cuando hace falta ya no se recuerda de dónde se partió — ahí
                    está el error de dedo. Con el número se decide sin pulsarlo. */}
                <button
                    className={`spe-undo-btn${sinCambios ? ' limpio' : ''}`}
                    onClick={() => !sinCambios && deshacer(field)}
                    disabled={sinCambios || elOriginal === null}
                    title={sinCambios
                        ? `Sin cambios: sigue en ${fmtP(elOriginal ?? 0)}`
                        : `Devolver a ${fmtP(elOriginal ?? 0)}, como estaba al abrir`}
                >
                    ↩ {elOriginal !== null ? fmtP(elOriginal) : '—'}
                </button>
            </div>
        );
    };

    return (
        <div className="spe-root">

            {/* Toast */}
            {toastMsg && <div className="spe-toast">{toastMsg}</div>}

            {/* ── BIG TIMELINE ── */}
            <div className="spe-tl-section">
                <div className="spe-tl-top">
                    <span className="spe-tl-title">
                        ⬤ Video timeline — clic para elegir, arrastra para mover
                        {elegido && (
                            <span className="spe-tl-elegido">
                                · {elegido === 'start' ? 'INICIO' : 'FIN'} elegido:
                                {' '}←→ una décima · Shift un segundo · Ctrl diez
                            </span>
                        )}
                    </span>
                    <button
                        className={`spe-oir-todo ${previewing === 'todo' ? 'sonando' : ''}`}
                        onClick={() => (previewing === 'todo' ? paraDeOir() : oyeElFragmento())}
                        title="Reproduce el fragmento de principio a fin y se para en su final"
                    >
                        {previewing === 'todo' ? '⏸ Parar' : '▶ Oír el fragmento entero'}
                    </button>
                    <span className="spe-dur-badge">⏱ {fmtP(segEnd - segStart)}</span>
                </div>

                <div className="spe-tl-wrap">
                    <div className="spe-tl-track" ref={trackRef}>
                        {/* Hasta dónde se puede llegar. Lo gris de los lados es
                            del vecino: el handle no entra ahí. Verlo evita
                            arrastrar contra un tope sin saber por qué frena. */}
                        <div className="spe-veda izq"
                             style={{ width: `${clamp((limites.start.min / total) * 100, 0, 100)}%` }} />
                        <div className="spe-veda der"
                             style={{ left: `${clamp((limites.end.max / total) * 100, 0, 100)}%` }} />
                        <div
                            className="spe-tl-region"
                            style={{ left: `${lp}%`, width: `${wp}%` }}
                        >
                            <div className="spe-region-s" />
                            <div className="spe-region-e" />
                        </div>

                        <div
                            className={`spe-handle start ${elegido === 'start' ? 'elegido' : ''} `
                                + `${moviendo === 'start' ? 'moviendo' : ''}`}
                            style={{ left: `${lp}%` }}
                            onMouseDown={e => onTlDown(e, 'start')}
                            onTouchStart={e => onTlDown(e, 'start')}
                            title="Clic para elegirlo · arrastra para moverlo · flechas para afinar"
                        >
                            <div className="spe-hline" /><div className="spe-hline" /><div className="spe-hline" />
                        </div>

                        <div
                            className={`spe-handle end ${elegido === 'end' ? 'elegido' : ''} `
                                + `${moviendo === 'end' ? 'moviendo' : ''}`}
                            style={{ left: `${ep}%` }}
                            onMouseDown={e => onTlDown(e, 'end')}
                            onTouchStart={e => onTlDown(e, 'end')}
                            title="Clic para elegirlo · arrastra para moverlo · flechas para afinar"
                        >
                            <div className="spe-hline" /><div className="spe-hline" /><div className="spe-hline" />
                        </div>
                    </div>
                </div>

                <div className="spe-tl-labels">
                    <span className="spe-tl-l">0:00</span>
                    <span className="spe-tl-l">{fmtM(total / 2)}</span>
                    <span className="spe-tl-l">{fmtM(total)}</span>
                </div>
            </div>

            {/* ── CONTROLS ── */}
            <div className="spe-controls">
                {renderField('start')}
                {renderField('end')}
            </div>

            {/* ── SMART PROPAGATION PANEL ── */}
            <div className="spe-propagation">
                <button
                    className={`spe-link-toggle ${linked ? 'linked' : 'unlinked'}`}
                    onClick={() => setLinked(l => !l)}
                    title={linked ? 'Propagar cambios a segmentos adyacentes (click para desactivar)' : 'Propagación desactivada (click para activar)'}
                >
                    {linked ? '🔗 Vinculado' : '✂️ Libre'}
                </button>

                {linked && (
                    <div className="spe-prop-info">
                        {prevSeg && (
                            <span className="spe-prop-badge prev">
                                ← #{prevSeg.id + 1} FIN se moverá con INICIO
                            </span>
                        )}
                        {nextSeg && (
                            <span className="spe-prop-badge next">
                                #{nextSeg.id + 1} INICIO se moverá con FIN →
                            </span>
                        )}
                        {!prevSeg && !nextSeg && (
                            <span className="spe-prop-badge none">Sin segmentos adyacentes</span>
                        )}
                    </div>
                )}

                {!linked && (
                    <span className="spe-prop-badge none">
                        Modo libre — segmentos adyacentes no se actualizan
                    </span>
                )}
            </div>

            {/* ── PREVIEW HINT ── */}
            <div className="spe-hint">
                Clic en un handle lo elige · arrastrar lo mueve · ←→ afina una décima
                (Shift un segundo, Ctrl diez)
                <br />
                🎧 oye los 5 s antes del corte · ▶ oye el fragmento entero ·
                🔗 mueve con él al fragmento vecino
                <br />
                Los lados en gris son del vecino: el handle no entra ahí.
            </div>
        </div>
    );
}